import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { additionalIncomeReportAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './AdditionalIncomeReportCreate.css';

const AdditionalIncomeReportEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    reportDate: new Date().toISOString().split('T')[0],
    incomeDescriptionAr: '',
    incomeDescriptionEng: '',
    amount: '',
    recipientNameAr: '',
    recipientNameEng: '',
    notes: '',
    isActive: 'true',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadReport();
  }, [id]);

  const loadReport = async () => {
    try {
      setLoadingData(true);
      const response = await additionalIncomeReportAPI.getById(id);
      const data = response.data;
      setFormData({
        reportDate: data.reportDate ? new Date(data.reportDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        incomeDescriptionAr: data.incomeDescriptionAr || '',
        incomeDescriptionEng: data.incomeDescriptionEng || '',
        amount: data.amount || '',
        recipientNameAr: data.recipientNameAr || '',
        recipientNameEng: data.recipientNameEng || '',
        notes: data.notes || '',
        isActive: data.isActive ? 'true' : 'false',
      });
    } catch (err) {
      setErrorMessage(t('additionalIncomeReport.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.reportDate.trim()) {
      newErrors.reportDate = t('additionalIncomeReport.edit.errors.dateRequired');
    }
    if (!formData.incomeDescriptionAr.trim()) {
      newErrors.incomeDescriptionAr = t('additionalIncomeReport.edit.errors.incomeDescriptionArRequired');
    }
    if (!formData.incomeDescriptionEng.trim()) {
      newErrors.incomeDescriptionEng = t('additionalIncomeReport.edit.errors.incomeDescriptionEngRequired');
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('additionalIncomeReport.edit.errors.amountRequired');
    }
    if (!formData.recipientNameAr.trim()) {
      newErrors.recipientNameAr = t('additionalIncomeReport.edit.errors.recipientNameArRequired');
    }
    if (!formData.recipientNameEng.trim()) {
      newErrors.recipientNameEng = t('additionalIncomeReport.edit.errors.recipientNameEngRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        isActive: formData.isActive === 'true',
        notes: formData.notes || null,
      };
      await additionalIncomeReportAPI.update(id, submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/additional-income-report');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('additionalIncomeReport.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('additionalIncomeReport.edit.fields.date')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.reportDate ? 'is-invalid' : ''}`}
                  name="reportDate"
                  value={formData.reportDate}
                  onChange={handleChange}
                />
              </div>
              {errors.reportDate && (
                <div className="error-message">{errors.reportDate}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('additionalIncomeReport.edit.fields.amount')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`form-input ${errors.amount ? 'is-invalid' : ''}`}
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.edit.placeholders.amount')}
                />
              </div>
              {errors.amount && (
                <div className="error-message">{errors.amount}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('additionalIncomeReport.edit.fields.isActive')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <select
                  className="form-input"
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                >
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-sticky-note"></i> {t('additionalIncomeReport.edit.fields.notes')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-sticky-note input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.edit.placeholders.notes')}
                  maxLength={1000}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-globe"></i> {t('additionalIncomeReport.edit.fields.incomeDescriptionEng')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-globe input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.incomeDescriptionEng ? 'is-invalid' : ''}`}
                  name="incomeDescriptionEng"
                  value={formData.incomeDescriptionEng}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.edit.placeholders.incomeDescriptionEng')}
                  maxLength={255}
                />
              </div>
              {errors.incomeDescriptionEng && (
                <div className="error-message">{errors.incomeDescriptionEng}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-language"></i> {t('additionalIncomeReport.edit.fields.incomeDescriptionAr')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-language input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.incomeDescriptionAr ? 'is-invalid' : ''}`}
                  name="incomeDescriptionAr"
                  value={formData.incomeDescriptionAr}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.edit.placeholders.incomeDescriptionAr')}
                  maxLength={255}
                />
              </div>
              {errors.incomeDescriptionAr && (
                <div className="error-message">{errors.incomeDescriptionAr}</div>
              )}
          </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-globe"></i> {t('additionalIncomeReport.edit.fields.recipientNameEng')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-globe input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.recipientNameEng ? 'is-invalid' : ''}`}
                  name="recipientNameEng"
                  value={formData.recipientNameEng}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.edit.placeholders.recipientNameEng')}
                  maxLength={255}
                />
              </div>
              {errors.recipientNameEng && (
                <div className="error-message">{errors.recipientNameEng}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-language"></i> {t('additionalIncomeReport.edit.fields.recipientNameAr')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-language input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.recipientNameAr ? 'is-invalid' : ''}`}
                  name="recipientNameAr"
                  value={formData.recipientNameAr}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.edit.placeholders.recipientNameAr')}
                  maxLength={255}
                />
              </div>
              {errors.recipientNameAr && (
                <div className="error-message">{errors.recipientNameAr}</div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/additional-income-report')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.loading') : t('additionalIncomeReport.edit.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('additionalIncomeReport.edit.messages.updateSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default AdditionalIncomeReportEdit;


